import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { withTranslation } from 'react-i18next';
import i18n from 'cityads-v3/i18n';

import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton';

import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';

import Table from 'cityads-v3/components/Table/Table';
import { FORMAT_CURRENCY, FORMAT_PERCENT } from 'cityads-v3/components/Format/NumberFormat';
import TargetValueFormat, { getTargetValue } from 'cityads-v3/components/Format/TargetValueFormat';
import Tooltip from 'cityads-v3/components/Tooltip/Tooltip';
import MediaQuery from 'react-responsive';
import OfferOption from 'cityads-v3/components/OfferOptions/OfferOption';
import Typography from '@mui/material/Typography';

import { breakpoints, leftMenuLargeWidth } from 'cityads-v3/utils/styleUtils';

import { getTargets } from 'cityads-v3/actions/offers';

import classes from './OfferTargetsTable.scss';

const serverMediaDefaults = {
  width: breakpoints.sm,
};

const columns = (t) => [{
  name: 'id',
  title: '#',
  w1: true,
  width: 68
}, {
  name: 'text',
  title: t('targetTitle'),
  width: 348,
}, {
  name: 'value',
  title: t('targetTariff'),
}];

const columnsWithRegion = (t) => [
  ...columns(t),
  {
    name: 'additional',
    title: '',
  }
];

@connect(
  (state, props) => ({
    language         : state.language.language,
    targets          : state.offers.targetsByOfferId[props.offerId],
    isStorageCookieOk: state.auth.isStorageCookieOk,
    isMenuDocked     : state.menu.docked,
  })
)
@withTranslation(['Offers'], { i18n, wait: !!window })
export default class OfferTargetsTable extends PureComponent {
  static propTypes = {
    t                : PropTypes.func.isRequired,
    dispatch         : PropTypes.func.isRequired,
    language         : PropTypes.string,
    offerId          : PropTypes.number.isRequired,
    targets          : PropTypes.oneOfType([PropTypes.object, PropTypes.arrayOf(PropTypes.object)]),
    isStorageCookieOk: PropTypes.bool,
    isMenuDocked     : PropTypes.bool,
    dense            : PropTypes.bool,
  }

  state = {
    expanded: false
  }

  onToggleExpand = () => {
    const { dispatch, offerId, targets } = this.props;

    if (!targets) {
      dispatch(getTargets(null, offerId));
    }

    this.setState({
      expanded: !this.state.expanded
    });

    setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    })
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    const { isStorageCookieOk, dispatch, offerId, targets, language } = newProps;

    if ((this.props.isStorageCookieOk !== isStorageCookieOk || this.props.language !== language) && targets) {
      // Targets were already loaded && auth status or language has changed
      dispatch(getTargets(null, offerId));
    }
  }

  replaceTargets(targets) {
    const targetsList = Array.isArray(targets) ? targets : [];

    // Ожидаем, что приходят только общие цели и индивидуальные для текущего юзера.
    // В таком случае, все индивидуальные цели, которым есть, кого заменять - заменяют
    const targetIdsToReplace = targetsList
      .reduce((res, curTarget) => {
        const { webmasters, replaced_target } = curTarget;

        if (webmasters && webmasters.length && replaced_target) {
          res[replaced_target] = true;
        }

        return res;
      }, {})
    ;

    return targetsList.filter(target => !targetIdsToReplace[target.id]);
  }

  groupTargetsByText(targets) {
    const targetsByText = this.replaceTargets(targets)
      .reduce((res, curTarget) => {
        const text = curTarget.text;
        const targetValue = getTargetValue(curTarget);

        const key = targetValue.format === FORMAT_PERCENT ? FORMAT_PERCENT : targetValue.currency;
        const originalKey = targetValue.format === FORMAT_PERCENT ? FORMAT_PERCENT : targetValue.originalCurrency;

        if (!res[text]) {
          res[text] = {
            byCurrency: {},
            byOriginalCurrency: {},
          };
        }

        if (!res[text].byCurrency[key]) {
          res[text].byCurrency[key] = [];
        }

        if (!res[text].byOriginalCurrency[originalKey]) {
          res[text].byOriginalCurrency[originalKey] = [];
        }

        const targetWithValue = { ...curTarget, ...targetValue };

        res[text].byCurrency[key].push(targetWithValue);
        res[text].byOriginalCurrency[originalKey].push(targetWithValue);

        return res;
      }, {})
    ;

    return Object.keys(targetsByText)
      .map(text => {
        const targetsWithCurrentText = targetsByText[text];
        let count = 0;
        let originalCount = 0;
        let hasOriginal = false;

        const targetValuesByType = Object.keys(targetsWithCurrentText.byCurrency)
          .sort()
          .map(type => {
            const targetsWithCurrentType = targetsWithCurrentText.byCurrency[type];

            count += targetsWithCurrentType.length;

            const isPercent = type === FORMAT_PERCENT;

            return {
              format: isPercent ? FORMAT_PERCENT : FORMAT_CURRENCY,
              currency: isPercent ? null : type,
              min: Math.min(...targetsWithCurrentType.map(item => item.min)),
              max: Math.max(...targetsWithCurrentType.map(item => item.max)),
            };
          })
        ;

        const targetValuesByOriginalType = Object.keys(targetsWithCurrentText.byOriginalCurrency)
          .sort()
          .map(type => {
            const targetsWithCurrentType = targetsWithCurrentText.byOriginalCurrency[type];

            originalCount += targetsWithCurrentType.length;

            if (!hasOriginal && targetsWithCurrentType.some(item => item.originalMin || item.originalMax)) {
              hasOriginal = true;
            }

            const isPercent = type === FORMAT_PERCENT;

            return {
              format: isPercent ? FORMAT_PERCENT : FORMAT_CURRENCY,
              currency: isPercent ? null : type,
              min: Math.min(...targetsWithCurrentType.map(item => item.originalMin || item.min)), // display original value if there was no conversion
              max: Math.max(...targetsWithCurrentType.map(item => item.originalMax || item.max)),
            };
          })
        ;

        return {
          text,
          count,
          originalCount,
          hasOriginal,
          targetValuesByType,
          targetValuesByOriginalType,
        };
      })
    ;
  }

  renderTargetValue({ targetValuesByType, targetValuesByOriginalType, hasOriginal }) {
    const content = targetValuesByType.map((targetValue, index) => (
      <div key={index}>
        <TargetValueFormat {...targetValue} />{(hasOriginal && targetValue.format !== FORMAT_PERCENT) ? ' *' : null}
      </div>
    ));

    return hasOriginal ?
      (
        <Tooltip isLight title={<>{this.props.t('conversionNotice')} {this.renderTargetValue({ targetValuesByType: targetValuesByOriginalType })}</>}>
          <div>{content}</div>
        </Tooltip>
      )
      :
      content
    ;
  }

  render() {
    const { targets, t, isMenuDocked, dense } = this.props;
    const { expanded } = this.state;
    const isLoading = !targets || targets.isLoading;

    const groupedTargets = this.groupTargetsByText(targets);
    const data = groupedTargets.map((item, num) => ({
      id        : (num + 1 < 10 ? '0' + (num+1) : num+1),
      text      : <span title={item.text}>{item.text}</span>,
      value     : this.renderTargetValue(item),
      additional: (item.count > 1) ? t('targetDepending') : '',
    }));
    const hasGrouped = groupedTargets.filter(item => item.count > 1).length > 0;

    const expander = (
      <Typography className={classes.targetsTableTitle} color="textPrimary">
        {t('payments')}
        {' '}
        <IconButton size="large" className={classes.targetsTableTitle__icon} onClick={this.onToggleExpand}>
          <ExpandMoreOutlinedIcon className={classNames(classes.targetsTableExpand, { [classes.targetsTableExpandExpanded]: expanded })} />
        </IconButton>
      </Typography>
    );

    return (
      <div className={classNames(classes.targetsTable, { [classes.expanded]: expanded, [classes.withGroupedTargets]: hasGrouped })}>
        <MediaQuery
          minWidth = {parseInt(breakpoints.md, 10) + (isMenuDocked ? parseInt(leftMenuLargeWidth, 10) : 0)}
          device   = {window ? null : serverMediaDefaults}
        >
          {(matches) => (
            matches ? (
              <div className={classes.options}>
                <Table
                  cols={expanded ? (hasGrouped ? columnsWithRegion(t) : columns(t)) : []}
                  rows={expanded ? data : []}
                  title={expander}
                  size="small"
                />
              </div>
            ) : (
              <div>
                {expander}
                {expanded ? (
                  <div className={classes.options}>
                    {data.map(option => (
                      <OfferOption
                        key={option.id}
                        title={option.text}
                        value={option.value}
                        dense={dense}
                      />
                    ))}
                  </div>
                ) : false}
              </div>
            )
          )}
        </MediaQuery>

        {expanded && isLoading && (
          <div className={classes.preloader}>
            <CircularProgress size={24} />
          </div>
        )}
      </div>
    );
  }
}
